import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CompanyResponse, LicenceResponse, ListResponse } from '@shared/schemas';

import type { Reask } from '../../_shared/domain/reask.domain';
import type { EnrichmentStats } from '../../_shared/domain/stats.domain';
import type { EnrichmentCandidate, EnrichmentType } from '../domain/candidates.domain';
import { BUCKET_ORDER, type BucketKey } from '../domain/cohort.domain';
import type { EnrichmentSession } from '../domain/sessions.domain';
import { initialPanelState, initialState } from './sessions.state';

/**
 * Sessions slice. Reducers are pure state transitions only; all I/O (polling,
 * the panel's fetches, the create write) lives in the saga, which dispatches
 * these transitions as it progresses. The `panel*` field actions double as the
 * saga's fetch triggers (it watches their types).
 */
const slice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {
    // ── Sessions table lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<EnrichmentSession[]>) => {
      state.rows = action.payload;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ── KPI stats (polled; null until first load) ──
    statsLoaded: (state, action: PayloadAction<EnrichmentStats>) => {
      state.stats = action.payload;
    },

    // ── New Session panel: open/close + form fields ──
    panelOpened: (state) => {
      // Fresh form each open (mirrors the old reset-on-open effect).
      state.panel = { ...initialPanelState, open: true };
      state.createStatus = 'idle';
      state.createError = null;
      state.createdSessionId = null;
    },
    panelClosed: (state) => {
      state.panel.open = false;
    },
    /**
     * Open the panel on a question a past run already answered.
     *
     * Not a retry: no target ids travel, only the question (connector, scope
     * and criteria). The cohort is recomputed against today's pool, which is the
     * point, and `reaskedFrom` keeps what it matched then so the panel can state
     * the gap before anything runs.
     *
     * The buckets are read out of the stored criteria and fall back to the
     * defaults when a run recorded none, so re-asking an old session lands on a
     * working panel rather than an empty one.
     *
     * A cap recorded by an older run is deliberately dropped: the field that set
     * it no longer exists, and re-applying a ceiling with nothing on screen to
     * show or lift it would silently shorten the run. The row's own recipe still
     * reads "first N"; that is history, and stays true.
     */
    panelReopenedFromRun: (state, action: PayloadAction<Reask>) => {
      const { type, licenceId, listId, criteria, previousCount, previousAt } = action.payload;
      const buckets = (criteria.buckets ?? []).filter((key): key is BucketKey =>
        (BUCKET_ORDER as readonly string[]).includes(key),
      );

      state.panel = {
        ...initialPanelState,
        open: true,
        type,
        licenceId,
        listId,
        filterTypes: criteria.types ?? [],
        currentOnly: criteria.currentOnly ?? false,
        includedBuckets: buckets.length > 0 ? buckets : initialPanelState.includedBuckets,
        reaskedFrom: { previousCount, previousAt },
      };
      state.createStatus = 'idle';
      state.createError = null;
      state.createdSessionId = null;
    },
    panelTypeChanged: (state, action: PayloadAction<EnrichmentType>) => {
      // Switching connector rebuilds the pool from scratch, so the filters and
      // the cohort go back to their defaults with it.
      state.panel.type = action.payload;
      state.panel.currentOnly = false;
      state.panel.filterTypes = [];
      state.panel.includedBuckets = initialPanelState.includedBuckets;
    },
    /**
     * Tick or untick one bucket. Ticking `fresh` is what sends `force`; there
     * is no separate switch, so the two cannot contradict each other.
     */
    panelBucketToggled: (state, action: PayloadAction<BucketKey>) => {
      const included = state.panel.includedBuckets;
      state.panel.includedBuckets = included.includes(action.payload)
        ? included.filter((key) => key !== action.payload)
        : [...included, action.payload];
    },
    panelLicenceChanged: (state, action: PayloadAction<string | null>) => {
      state.panel.licenceId = action.payload;
      state.panel.listId = null; // list is scoped to the licence
    },
    panelListChanged: (state, action: PayloadAction<string | null>) => {
      state.panel.listId = action.payload;
    },
    panelCurrentOnlyToggled: (state, action: PayloadAction<boolean>) => {
      state.panel.currentOnly = action.payload;
    },
    panelFilterTypesChanged: (state, action: PayloadAction<string[]>) => {
      state.panel.filterTypes = action.payload;
    },

    // ── New Session panel: fetched dropdown data ──
    licencesLoading: (state) => {
      state.panel.licencesLoading = true;
    },
    licencesLoaded: (state, action: PayloadAction<LicenceResponse[]>) => {
      state.panel.licences = action.payload;
      state.panel.licencesLoading = false;
    },
    profileTypesLoading: (state) => {
      state.panel.profileTypesLoading = true;
    },
    profileTypesLoaded: (state, action: PayloadAction<string[]>) => {
      state.panel.profileTypes = action.payload;
      state.panel.profileTypesLoading = false;
    },
    listsLoading: (state) => {
      state.panel.listsLoading = true;
    },
    listsLoaded: (state, action: PayloadAction<ListResponse[]>) => {
      state.panel.lists = action.payload;
      state.panel.listsLoading = false;
    },
    entitiesLoading: (state) => {
      state.panel.entitiesLoading = true;
    },
    candidatesLoaded: (state, action: PayloadAction<EnrichmentCandidate[]>) => {
      state.panel.candidates = action.payload;
      state.panel.entitiesLoading = false;
    },
    companiesLoaded: (state, action: PayloadAction<CompanyResponse[]>) => {
      state.panel.companies = action.payload;
      state.panel.entitiesLoading = false;
    },

    // ── Create-session write lifecycle ──
    createStarted: (state) => {
      state.createStatus = 'submitting';
      state.createError = null;
    },
    createSucceeded: (state, action: PayloadAction<EnrichmentSession>) => {
      state.createStatus = 'succeeded';
      state.createdSessionId = action.payload.id;
    },
    createFailed: (state, action: PayloadAction<string>) => {
      state.createStatus = 'failed';
      state.createError = action.payload;
    },
    createReset: (state) => {
      state.createStatus = 'idle';
      state.createError = null;
      state.createdSessionId = null;
    },
  },
});

export const sessionsActions = slice.actions;
export const sessionsReducer = slice.reducer;
